import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { ProductImagePlaceholder } from "@/components/ProductImagePlaceholder";
import { trackProductView } from "@/utils/analytics";

type ProductModalProps = {
  isOpen: boolean;
  onClose: () => void;
  product: {
    name: string;
    price: number;
    image?: {
      src: string;
      alt: string;
    };
    images?: Array<{
      src: string;
      alt: string;
    }>;
    description?: string;
    badge?: string;
  };
  onAddToCart: () => void;
};

export function ProductModal({ isOpen, onClose, product, onAddToCart }: ProductModalProps) {
  // Build gallery array: prioritize images[], fallback to image, or empty array
  const galleryImages = product.images && product.images.length > 0
    ? product.images
    : product.image
    ? [product.image]
    : [];

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Reset to first image when modal opens or product changes
  useEffect(() => {
    setSelectedImageIndex(0);
    
    // Track product view when modal opens
    if (isOpen && product.name && product.price) {
      trackProductView(product.name.toLowerCase().replace(/\s+/g, '-'), product.name, product.price);
    }
  }, [product.name, product.price, isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen || galleryImages.length <= 1) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        setSelectedImageIndex((prev) => 
          prev === 0 ? galleryImages.length - 1 : prev - 1
        );
      } else if (e.key === "ArrowRight") {
        setSelectedImageIndex((prev) => 
          prev === galleryImages.length - 1 ? 0 : prev + 1
        );
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, galleryImages.length]);

  const currentImage = galleryImages[selectedImageIndex];
  const hasMultipleImages = galleryImages.length > 1;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="space-y-5 animate-[fadeInScale_0.25s_ease-out]">
        {/* Product Badge */}
        {product.badge && (
          <span className="inline-flex items-center rounded-full bg-emerald-500/15 border border-emerald-300/40 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-emerald-300">
            {product.badge}
          </span>
        )}

        {/* Product Name */}
        <h2 className="text-2xl sm:text-3xl font-bold text-emerald-100">
          {product.name}
        </h2>

        {/* Image Gallery */}
        {galleryImages.length > 0 ? (
          <div className="flex flex-col md:flex-row gap-3">
            {/* Thumbnail Column - Desktop: left side, Mobile: bottom row */}
            {hasMultipleImages && (
              <div className="order-2 md:order-1 flex md:flex-col gap-2 overflow-x-auto md:overflow-y-auto md:max-h-[400px] scrollbar-thin scrollbar-thumb-emerald-500/30 scrollbar-track-slate-800/50">
                {galleryImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      index === selectedImageIndex
                        ? "border-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.6)]"
                        : "border-slate-700/50 hover:border-emerald-400/50"
                    }`}
                    aria-label={`View image ${index + 1}`}
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="w-full h-full object-cover"
                      style={{ 
                        imageRendering: '-webkit-optimize-contrast',
                        backfaceVisibility: 'hidden'
                      }}
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Main Image Display */}
            <div className="order-1 md:order-2 flex-1 w-full aspect-[4/3] overflow-hidden rounded-xl bg-gradient-to-br from-slate-800/60 to-slate-900/80 border border-white/5 shadow-xl">
              {currentImage && currentImage.src !== "/images/placeholder.png" ? (
                <img
                  src={currentImage.src}
                  alt={currentImage.alt}
                  className="w-full h-full object-contain p-4 md:p-6"
                  style={{ 
                    imageRendering: '-webkit-optimize-contrast',
                    backfaceVisibility: 'hidden',
                    transform: 'translateZ(0)'
                  }}
                />
              ) : (
                <ProductImagePlaceholder aspectRatio="aspect-[4/3]" />
              )}
            </div>
          </div>
        ) : (
          <ProductImagePlaceholder aspectRatio="aspect-[4/3]" />
        )}

        {/* Image Counter (only show if multiple images) */}
        {hasMultipleImages && (
          <div className="text-center text-xs text-slate-400">
            Image {selectedImageIndex + 1} of {galleryImages.length}
          </div>
        )}

        {/* Price */}
        <div className="flex items-baseline gap-2">
          <span className="text-xs uppercase tracking-wider text-slate-400">Price</span>
          <span className="text-3xl font-bold text-emerald-300">
            £{product.price.toFixed(2)}
          </span>
        </div>

        {/* Description */}
        <div className="rounded-xl bg-slate-800/40 border border-white/5 p-5">
          <h3 className="text-sm font-semibold text-emerald-300 uppercase tracking-wider mb-3">
            Product Description
          </h3>
          <p className="text-sm leading-relaxed text-slate-300">
            {product.description || "Product description coming soon"}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            onClick={() => {
              onAddToCart();
              onClose();
            }}
            className="flex-1 rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 text-slate-900 font-semibold shadow-[0_0_40px_rgba(45,255,196,0.35)] hover:shadow-[0_0_50px_rgba(45,255,196,0.55)] transition-all py-6"
          >
            Add to cart
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            className="rounded-full border-2 border-emerald-300/50 text-emerald-300 hover:bg-emerald-300/10 hover:text-emerald-200 px-6 py-6"
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
